import { useEffect, useState } from "react";
import { HardHat, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { authApi, setToken, formatApiErrorDetail } from "../lib/auth";

export default function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAdmin, setShowAdmin] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(null);

  useEffect(() => {
    let active = true;
    authApi.googleStatus().then((status) => {
      if (!active) return;
      setGoogleAvailable(Boolean(status.configured));
      if (!status.configured) setShowAdmin(true);
    }).catch(() => {
      if (active) { setGoogleAvailable(false); setShowAdmin(true); }
    });
    return () => { active = false; };
  }, []);

  const googleError = new URLSearchParams(window.location.search).get("google_login");
  const googleMessage = googleError === "denied"
    ? "Account Google non autorizzato. Chiedi all’amministratore di abilitarlo."
    : googleError && googleError !== "connected"
      ? "Accesso Google non riuscito. Riprova."
      : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const r = await authApi.login(password);
      setToken(r.token);
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, "Accesso non riuscito"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4 sm:p-6">
      <form onSubmit={submit} data-testid="login-form" className="w-full max-w-sm bg-slate-800 border border-slate-700 rounded-2xl p-6 sm:p-8 shadow-lg">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-orange-600 flex items-center justify-center"><HardHat className="w-6 h-6 text-white" /></div>
          <div>
            <div className="font-display font-bold text-white text-lg leading-none">Ellebierre S.R.L.</div>
            <div className="text-[10px] text-orange-400 font-mono tracking-widest uppercase mt-1">Area privata</div>
          </div>
        </div>
        <h1 className="text-white font-display font-semibold text-xl">Accesso riservato</h1>
        <p className="text-slate-400 text-sm mt-1 mb-6">Accedi con il tuo account Google autorizzato da ELLEBIERRE.</p>

        <a href={authApi.googleLoginUrl()} onClick={(e) => {
          if (googleAvailable !== true) {
            e.preventDefault();
            setError(googleAvailable === false
              ? "Accesso Google da attivare: Impostazioni → Sicurezza → Login Google (amministratore)."
              : "Verifica configurazione Google in corso. Riprova.");
          }
        }} data-testid="google-login-button"
          className="w-full inline-flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-slate-900 font-semibold py-3 rounded-lg transition-colors">
          <span className="font-bold text-lg">G</span> Accedi con Google
        </a>
        {googleAvailable === false && <p className="text-xs text-amber-300 mt-2">Il login Google è indipendente da Gmail. L'amministratore deve configurare il client OAuth in Impostazioni → Sicurezza.</p>}

        {(googleMessage || error) && <p data-testid="login-error" className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{googleMessage || error}</p>}

        <button type="button" onClick={() => setShowAdmin((v) => !v)}
          className="mt-6 w-full text-xs text-slate-400 hover:text-slate-200 underline underline-offset-4">
          {showAdmin ? "Nascondi accesso amministrativo" : "Accesso amministrativo"}
        </button>

        {showAdmin && <>
        <div className="my-4 border-t border-white/10" />
        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">Password amministratore</label>
        <div className="relative">
          <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoFocus autoComplete="current-password"
            data-testid="login-password-input" placeholder="••••••••"
            className="w-full pl-9 pr-10 py-3 bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500" />
          <button type="button" onClick={() => setShow((s) => !s)} data-testid="login-toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button type="submit" disabled={loading || !password} data-testid="login-submit-button"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors active:scale-[0.99]">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Entra come amministratore
        </button>
        </>}
      </form>
    </div>
  );
}
